import { TerritoryType, type TerritoryTypeValue, TILE_SIZE } from '@dreamgame/shared';
import { Graphics } from 'pixi.js';
import { selectedTerritoryId, type TerritoryData, territories } from '../../state/territories';

// ============================================================================
// TerritoryLayer - Renders territory boundaries and protection zones
// ============================================================================

let graphics: Graphics;

// Territory colors based on type
const TERRITORY_COLORS: Record<TerritoryTypeValue, { fill: number; stroke: number }> = {
  [TerritoryType.HOME]: { fill: 0x3366cc, stroke: 0x5588ee },
  [TerritoryType.OUTPOST]: { fill: 0xcc6633, stroke: 0xee8855 },
};

export const createTerritoryLayer = (): { graphics: Graphics } => {
  graphics = new Graphics();
  return { graphics };
};

export const drawTerritories = (): void => {
  graphics.clear();

  const selectedId = selectedTerritoryId.value;
  if (selectedId === null) {
    return;
  }

  // Only draw the selected territory
  const territory = territories.value.get(selectedId);
  if (territory) {
    drawTerritoryZone({ territory });
  }
};

const drawTerritoryZone = ({ territory }: { territory: TerritoryData }): void => {
  const colors = TERRITORY_COLORS[territory.territoryType];
  const centerWorldX = territory.centerX * TILE_SIZE + TILE_SIZE / 2;
  const centerWorldY = territory.centerY * TILE_SIZE + TILE_SIZE / 2;

  // Calculate the territory bounds (radius is in tiles)
  const halfWidth = ((territory.radius * 2 + 1) * TILE_SIZE) / 2;
  const halfHeight = ((territory.radius * 2 + 1) * TILE_SIZE) / 2;

  const x = centerWorldX - halfWidth;
  const y = centerWorldY - halfHeight;
  const width = halfWidth * 2;
  const height = halfHeight * 2;

  // Draw fill
  graphics.setFillStyle({ color: colors.fill, alpha: 0.2 });
  graphics.rect(x, y, width, height);
  graphics.fill();

  // Draw border
  graphics.setStrokeStyle({ width: 3, color: colors.stroke, alpha: 0.8 });
  graphics.rect(x, y, width, height);
  graphics.stroke();

  // Draw corner markers
  drawCornerMarkers({ x, y, width, height, color: colors.stroke });

  // Draw dashed inner border for visual interest
  drawDashedBorder({
    x: x + 4,
    y: y + 4,
    width: width - 8,
    height: height - 8,
    color: colors.stroke,
  });
};

const drawCornerMarkers = ({
  x,
  y,
  width,
  height,
  color,
}: {
  x: number;
  y: number;
  width: number;
  height: number;
  color: number;
}): void => {
  const markerSize = 12;
  const markerWidth = 3;

  graphics.setStrokeStyle({ width: markerWidth, color, alpha: 1 });

  // Top-left corner
  graphics.moveTo(x, y + markerSize);
  graphics.lineTo(x, y);
  graphics.lineTo(x + markerSize, y);
  graphics.stroke();

  // Top-right corner
  graphics.moveTo(x + width - markerSize, y);
  graphics.lineTo(x + width, y);
  graphics.lineTo(x + width, y + markerSize);
  graphics.stroke();

  // Bottom-left corner
  graphics.moveTo(x, y + height - markerSize);
  graphics.lineTo(x, y + height);
  graphics.lineTo(x + markerSize, y + height);
  graphics.stroke();

  // Bottom-right corner
  graphics.moveTo(x + width - markerSize, y + height);
  graphics.lineTo(x + width, y + height);
  graphics.lineTo(x + width, y + height - markerSize);
  graphics.stroke();
};

const drawDashedBorder = ({
  x,
  y,
  width,
  height,
  color,
}: {
  x: number;
  y: number;
  width: number;
  height: number;
  color: number;
}): void => {
  const dashLength = 8;
  const gapLength = 6;

  graphics.setStrokeStyle({ width: 1, color, alpha: 0.4 });

  // Top edge
  let currentX = x;
  while (currentX < x + width) {
    const endX = Math.min(currentX + dashLength, x + width);
    graphics.moveTo(currentX, y);
    graphics.lineTo(endX, y);
    graphics.stroke();
    currentX += dashLength + gapLength;
  }

  // Bottom edge
  currentX = x;
  while (currentX < x + width) {
    const endX = Math.min(currentX + dashLength, x + width);
    graphics.moveTo(currentX, y + height);
    graphics.lineTo(endX, y + height);
    graphics.stroke();
    currentX += dashLength + gapLength;
  }

  // Left edge
  let currentY = y;
  while (currentY < y + height) {
    const endY = Math.min(currentY + dashLength, y + height);
    graphics.moveTo(x, currentY);
    graphics.lineTo(x, endY);
    graphics.stroke();
    currentY += dashLength + gapLength;
  }

  // Right edge
  currentY = y;
  while (currentY < y + height) {
    const endY = Math.min(currentY + dashLength, y + height);
    graphics.moveTo(x + width, currentY);
    graphics.lineTo(x + width, endY);
    graphics.stroke();
    currentY += dashLength + gapLength;
  }
};
